import SolarPowerOutlinedIcon from "@mui/icons-material/SolarPowerOutlined";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Paper,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { memo } from "react";
import PropTypes from "prop-types";

import { wrapFormikHandleChange } from "../../../../../../infrastructure/utils/formatters";
import DropdownPicker from "../../../../../components/shared/dropdown";
import {
  applicantGoalOptions,
  FORM_FIELD_SIZE,
  locationUsageTypeOptions,
  normalizeEnumList,
  ownershipStatusOptions,
  platformAcquaintanceSourceOptions,
} from "./initialReviewConstants";
import { normalizeEnumDropdownList } from "../checkNewRequestPageUtils";
import { CRM_CARD_BODY_SX, CRM_CARD_HEADER_SX, CRM_CARD_SX, CRM_FIELD_GRID, CRM_SECTION_TITLE_SX } from "./initialReviewStyles";

const ProjectInfoCard = memo(
  ({
    values,
    formData,
    isEditable,
    fieldDisabled,
    onMakeFormikVariable,
    handleChange,
    setFieldValue,
    powerPlantTypeList,
  }) => {
    const plantKindList = normalizeEnumDropdownList(formData?.plantKind);
    const plantCapacityList = normalizeEnumDropdownList(formData?.plantCapacity);
    const plantKindLabel =
      values.plantKindTitle ||
      plantKindList.find((item) => String(item.value) === String(values.plantKind))
        ?.name ||
      values.plantKind ||
      "—";

    return (
      <Paper elevation={0} sx={CRM_CARD_SX}>
        <Box sx={CRM_CARD_HEADER_SX}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SolarPowerOutlinedIcon sx={{ fontSize: 20, color: "primary.main" }} />
            <Typography sx={CRM_SECTION_TITLE_SX}>اطلاعات پروژه</Typography>
          </Box>
        </Box>
        <Box sx={CRM_CARD_BODY_SX}>
          <Box sx={CRM_FIELD_GRID}>
            <TextField
              label="شناسه قبض"
              required
              size={FORM_FIELD_SIZE}
              onChange={wrapFormikHandleChange(handleChange)}
              disabled={fieldDisabled}
              {...onMakeFormikVariable("electricBoillNumber")}
            />

            <DropdownPicker
              size={FORM_FIELD_SIZE}
              label="ظرفیت نیروگاه"
              onChange={(event) => setFieldValue("plantCapacity", event.target.value)}
              list={plantCapacityList}
              filterable={false}
              disabled={fieldDisabled}
              {...onMakeFormikVariable("plantCapacity")}
            />

            {isEditable ? (
              <DropdownPicker
                size={FORM_FIELD_SIZE}
                label="کاربری محل"
                list={locationUsageTypeOptions}
                filterable={false}
                value={
                  values.locationUsageType != null && values.locationUsageType !== ""
                    ? String(values.locationUsageType)
                    : ""
                }
                onChange={(event) =>
                  setFieldValue("locationUsageType", event.target.value)
                }
                disabled={fieldDisabled}
                fullWidth
              />
            ) : (
              <TextField
                label="کاربری محل"
                value={values.locationUsageTypeTitle || "ثبت نشده"}
                disabled
                fullWidth
                size={FORM_FIELD_SIZE}
                variant="outlined"
              />
            )}

            {isEditable ? (
              <DropdownPicker
                size={FORM_FIELD_SIZE}
                label="نحوه آشنایی با پلتفرم"
                list={platformAcquaintanceSourceOptions}
                filterable={false}
                value={
                  values.platformAcquaintanceSource != null &&
                  values.platformAcquaintanceSource !== ""
                    ? String(values.platformAcquaintanceSource)
                    : ""
                }
                onChange={(event) =>
                  setFieldValue("platformAcquaintanceSource", event.target.value)
                }
                disabled={fieldDisabled}
                fullWidth
              />
            ) : (
              <TextField
                label="نحوه آشنایی با پلتفرم"
                value={values.platformAcquaintanceSourceTitle || "ثبت نشده"}
                disabled
                fullWidth
                size={FORM_FIELD_SIZE}
                variant="outlined"
              />
            )}

            {isEditable ? (
              <DropdownPicker
                size={FORM_FIELD_SIZE}
                label="وضعیت مالکیت"
                list={ownershipStatusOptions}
                filterable={false}
                value={
                  values.ownershipStatus != null && values.ownershipStatus !== ""
                    ? String(values.ownershipStatus)
                    : ""
                }
                onChange={(event) => setFieldValue("ownershipStatus", event.target.value)}
                disabled={fieldDisabled}
                fullWidth
              />
            ) : (
              <TextField
                label="وضعیت مالکیت"
                value={values.ownershipStatusTitle || "ثبت نشده"}
                disabled
                fullWidth
                size={FORM_FIELD_SIZE}
                variant="outlined"
              />
            )}

            {isEditable ? (
              <DropdownPicker
                size={FORM_FIELD_SIZE}
                label="نوع نیروگاه"
                list={normalizeEnumList(powerPlantTypeList)}
                filterable={false}
                value={
                  values.powerPlantType != null && values.powerPlantType !== ""
                    ? String(values.powerPlantType)
                    : ""
                }
                onChange={(event) => setFieldValue("powerPlantType", event.target.value)}
                disabled={fieldDisabled}
                fullWidth
              />
            ) : (
              <TextField
                label="نوع نیروگاه"
                value={values.powerPlantTypeTitle || "ثبت نشده"}
                disabled
                fullWidth
                size={FORM_FIELD_SIZE}
                variant="outlined"
              />
            )}

            <TextField
              label="مساحت قابل نصب (م²)"
              type="number"
              size={FORM_FIELD_SIZE}
              value={values.installableAreaSquareMeter ?? ""}
              onChange={(e) =>
                setFieldValue(
                  "installableAreaSquareMeter",
                  e.target.value.replace(/[^\d.]/g, "")
                )
              }
              disabled={fieldDisabled}
              fullWidth
              variant="outlined"
            />

            <TextField
              label="دسته نیروگاه"
              value={plantKindLabel}
              disabled
              fullWidth
              size={FORM_FIELD_SIZE}
              variant="outlined"
              InputProps={{ readOnly: true }}
            />

            <Box
              sx={{
                gridColumn: { sm: "1 / -1" },
                px: 1.25,
                py: 0.75,
                borderRadius: 1.5,
                bgcolor: values.needsLoan ? "warning.50" : "grey.50",
                border: "1px solid",
                borderColor: "divider",
              }}
            >
              {isEditable ? (
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      checked={Boolean(values.needsLoan)}
                      onChange={(event) =>
                        setFieldValue("needsLoan", event.target.checked)
                      }
                      disabled={fieldDisabled}
                      color="warning"
                      size="small"
                    />
                  }
                  label={
                    <Typography variant="body2" fontWeight={600}>
                      {values.needsLoan ? "نیاز به وام دارد" : "بدون نیاز به وام"}
                    </Typography>
                  }
                />
              ) : (
                <Typography variant="body2">
                  نیاز به وام: {values.needsLoan ? "بله" : "خیر"}
                </Typography>
              )}
            </Box>
          </Box>

          <Box sx={{ mt: 1.5 }}>
            {isEditable ? (
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" sx={{ mb: 0.75, fontSize: "0.8125rem" }}>
                  اهداف متقاضی
                </FormLabel>
                <FormGroup
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 0.25,
                  }}
                >
                  {applicantGoalOptions.map((goal) => {
                    const currentFlags = Number(values.applicantGoalFlags) || 0;
                    const checked = (currentFlags & goal.value) === goal.value;
                    return (
                      <FormControlLabel
                        key={goal.value}
                        control={
                          <Checkbox
                            checked={checked}
                            disabled={fieldDisabled}
                            size="small"
                            onChange={() => {
                              const nextFlags = checked
                                ? currentFlags & ~goal.value
                                : currentFlags | goal.value;
                              setFieldValue("applicantGoalFlags", nextFlags);
                            }}
                          />
                        }
                        label={
                          <Typography variant="caption">{goal.label}</Typography>
                        }
                      />
                    );
                  })}
                </FormGroup>
              </FormControl>
            ) : (
              <TextField
                label="هدف متقاضی"
                value={values.applicantGoalsTitle || "ثبت نشده"}
                disabled
                fullWidth
                size={FORM_FIELD_SIZE}
                variant="outlined"
              />
            )}
          </Box>

          {values.adminComment ? (
            <TextField
              sx={{ mt: 1.5 }}
              label="یادداشت متقاضی"
              value={values.adminComment}
              disabled
              fullWidth
              size={FORM_FIELD_SIZE}
              multiline
              minRows={2}
              variant="outlined"
            />
          ) : null}
        </Box>
      </Paper>
    );
  }
);

ProjectInfoCard.displayName = "ProjectInfoCard";

ProjectInfoCard.propTypes = {
  values: PropTypes.object.isRequired,
  formData: PropTypes.object,
  isEditable: PropTypes.bool,
  fieldDisabled: PropTypes.bool,
  onMakeFormikVariable: PropTypes.func.isRequired,
  handleChange: PropTypes.func,
  setFieldValue: PropTypes.func.isRequired,
  powerPlantTypeList: PropTypes.array,
};

export default ProjectInfoCard;
